import {
    StyledProgressCell,
    StyledReferringDoctorCell,
    StyledSelectCell,
    StyledStatusCell,
    StyledWrapper
} from '@pages/projects/style.ts'
import {Helmet} from 'react-helmet'
import i18n, {useTranslation} from '@/translations/i18n.tsx'
import {ButtonLink} from '@components/ui/button-link/ButtonLink.tsx'
import {routes} from '@utilities/constants/routes.tsx'
import {
    AlertCircleIcon,
    AlertTriangleIcon,
    Download01Icon,
    PhoneIcon,
    PlusIcon,
    Repeat02Icon
} from '@components/ui/icon/Icon.tsx'
import {PageHeading} from '@components/ui/page-atoms/PageAtoms.tsx'
import {FiltersBar} from '@components/ui/filters-bar/FiltersBar.tsx'
import {TableInputSearch} from '@components/commons/table-input-search/TableInputSearch.tsx'
import {SelectedRowsText} from '@components/commons/selected-rows-text/SelectedRowsText.tsx'
import {Button} from '@components/ui/button/Button.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {ChangeEvent, MouseEvent, useRef, useState} from 'react'
import {useParsedSearchParams} from '@hooks/useParsedSearchParams.ts'
import {z} from 'zod'
import {
    debounce,
    filtersParamToPayload,
    getIsRowSelected,
    sortParamToPayload,
    workServiceToLabel,
    columnVisibilitySearchParamsToPayload,
    columnVisibilityToSearchParams
} from '@utilities/helpers.ts'
import {useProjects} from '@/features/projects/queries/useProjects'
import {ColumnVisibilityMenu} from '@components/commons/columns-visibility-menu/ColumnVisibilityMenu.tsx'
import {ErrorBox} from '@components/commons/error-box/ErrorBox.tsx'
import {Table} from '@components/commons/table/Table.tsx'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {TableBodyRow} from '@components/ui/table-body-row/TableBodyRow.tsx'
import {generatePath, Link, useNavigate} from 'react-router-dom'
import {TableBodyCell, TableBodyCellProps, TableBodyCellText} from '@components/ui/table-atoms/TableAtoms.tsx'
import {SelectRowCheckbox} from '@components/ui/select-row-checkbox/SelectRowCheckbox.tsx'
import {SelectColumnHeader} from '@components/ui/select-column-header/SelectColumnHeader.tsx'
import {MoreActionsCell} from '@components/ui/more-actions-cell/MoreActionsCell.tsx'
import {RowMenu} from '@/features/projects/components/row-menu/RowMenu.tsx'
import {formatLocaleDate} from '@/dayjs.ts'
import {
    columnNameToLabel,
    projectProgressStatusToIcon,
    projectProgressStatusToLabel,
    projectSectorToLabel,
    projectStatusToLabel,
    projectTypeToLabel
} from '@/features/projects/utils.ts'
import {StatusBullet} from '@/features/projects/components/status-bullet/StatusBullet.tsx'
import {Badge} from '@/components/ui/badge/Badge'
import {ProgressBar} from '@/components/ui/progress-bar/ProgressBar'
import {TableFilters} from '@components/commons/table-filters/TableFilters.tsx'
import {ProjectsColumn} from '@/features/projects/types'
import {SortingMenu} from '@/components/commons/sorting-menu/SortingMenu'
import {Text} from '@/components/ui/text/Text'
import {useTheme} from 'styled-components'
import {ViewSelect} from '@/features/views/views-select/ViewSelect'
import {Contact} from '@/types'
import {StyledContactCell} from '../patients/style'
import {MenuButton, MenuProvider} from '@ariakit/react'
import {Menu, MenuGroup, MenuItem} from '@/components/ui/menu-atoms/MenuAtoms'
import {usePermissions} from '@/features/auth/store/store'
import {httpExportProjects} from '@/features/projects/http/projects.http'
import {ExportModal} from '@/components/commons/export-modal/ExportModal'

const makeColumnsMap = () => [
    {id: ProjectsColumn.enum.fullName, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.fullName])},
    {id: ProjectsColumn.enum.sector, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.sector])},
    {id: ProjectsColumn.enum.birthDate, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.birthDate])},
    {id: ProjectsColumn.enum.age, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.age])},
    {id: ProjectsColumn.enum.serviceType, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.serviceType])},
    {id: ProjectsColumn.enum.type, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.type])},
    {id: ProjectsColumn.enum.code, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.code])},
    {id: ProjectsColumn.enum.startDate, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.startDate])},
    {id: ProjectsColumn.enum.durationDays, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.durationDays])},
    {id: ProjectsColumn.enum.expirationDate, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.expirationDate])},
    {id: ProjectsColumn.enum.status, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.status])},
    {id: ProjectsColumn.enum.isRenewed, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.isRenewed])},
    {id: ProjectsColumn.enum.endDate, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.endDate])},
    {id: ProjectsColumn.enum.isSuspended, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.isSuspended])},
    {id: ProjectsColumn.enum.suspensionDays, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.suspensionDays])},
    {
        id: ProjectsColumn.enum.completedAccessesPerCent,
        label: i18n.t(columnNameToLabel[ProjectsColumn.enum.completedAccessesPerCent])
    },
    {id: ProjectsColumn.enum.absenceDays, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.absenceDays])},
    {id: ProjectsColumn.enum.progressStatus, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.progressStatus])},
    {
        id: ProjectsColumn.enum.referringDoctorId,
        label: i18n.t(columnNameToLabel[ProjectsColumn.enum.referringDoctorId])
    },
    {id: ProjectsColumn.enum.telephone, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.telephone])},
    {id: ProjectsColumn.enum.taxCode, label: i18n.t(columnNameToLabel[ProjectsColumn.enum.taxCode])}
]

export const Projects = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const {palette} = useTheme()
    const columnsMap = makeColumnsMap()
    const navigate = useNavigate()
    const inputSearchRef = useRef<HTMLInputElement | null>(null)
    const {searchParams, setSearchParams} = useParsedSearchParams(
        z.object({
            limit: z.coerce.number().int().positive().default(50),
            page: z.coerce.number().int().positive().default(1),
            search: z.string().trim().optional(),
            filters: z.string().optional(),
            view: z.string().min(1).default('all'),
            sorts: z.string().optional(),
            columnVisibility: z.string().optional()
        })
    )
    const projectsQuery = useProjects(
        {
            limit: searchParams.limit,
            page: searchParams.page,
            search: searchParams.search
        },
        {
            filters: filtersParamToPayload(searchParams.filters)?.filters,
            sorts: sortParamToPayload(searchParams.sorts)
        }
    )
    const [isSelectAll, setIsSelectAll] = useState(false)
    const [unselectedProjectsIds, setUnselectedProjectsIds] = useState<string[]>([])
    const [selectedProjectsIds, setSelectedProjectsIds] = useState<string[]>([])
    const selectedProjectsCount = isSelectAll
        ? projectsQuery.persistedPagination.total - unselectedProjectsIds.length
        : selectedProjectsIds.length
    const onSearch = debounce((event: ChangeEvent<HTMLInputElement>) => {
        setSearchParams({search: event.target.value || undefined, page: 1})
    }, 1000)
    const resetSelectionStates = () => {
        setIsSelectAll(false)
        setSelectedProjectsIds([])
        setUnselectedProjectsIds([])
    }
    const columnVisibility =
        columnVisibilitySearchParamsToPayload(ProjectsColumn, searchParams.columnVisibility) ??
        Object.fromEntries(
            ProjectsColumn.options.map(column => [
                column,
                column != ProjectsColumn.enum.telephone && column != ProjectsColumn.enum.taxCode
            ])
        )

    return (
        <StyledWrapper>
            <Helmet title={t('projects:meta_title')} />
            <PageHeading justify="space-between">
                <h2>{t('projects:title')}</h2>
                {permissions?.projects?.write && (
                    <ButtonLink variant="primaryNeutral" to={routes.CREATE_PROJECT.path}>
                        <PlusIcon />
                        {t('commons:rehabilitation_project', {count: 1})}
                    </ButtonLink>
                )}
            </PageHeading>
            <FiltersBar>
                <Flexbox align="center" gap={2}>
                    <TableInputSearch
                        ref={inputSearchRef}
                        defaultValue={searchParams.search}
                        onChange={onSearch}
                        placeholder={t('commons:search_patient')}
                    />
                    {selectedProjectsCount >= 1 && (
                        <>
                            <SelectedRowsText count={selectedProjectsCount} />
                            <ExportModal
                                entity="projects"
                                trigger={
                                    <Button variant="ghost" size="sm">
                                        <Download01Icon />
                                        {t('commons:export')}
                                    </Button>
                                }
                                asyncExport
                                fileName="PRI"
                                promise={({extension, mode, customColumns}) =>
                                    httpExportProjects({
                                        payload: {
                                            selectedProjectIds: selectedProjectsIds,
                                            exceptedProjectIds: unselectedProjectsIds,
                                            type: extension,
                                            filters: filtersParamToPayload(searchParams.filters)?.filters,
                                            fields:
                                                mode == 'custom'
                                                    ? customColumns
                                                    : mode == 'view'
                                                      ? ProjectsColumn.exclude([
                                                            'select',
                                                            'moreActions'
                                                        ]).options.filter(column => columnVisibility[column])
                                                      : ProjectsColumn.exclude(['select', 'moreActions']).options,
                                            sorts: sortParamToPayload(searchParams.sorts)
                                        }
                                    })
                                }
                                selectedCount={selectedProjectsCount}
                                allowedExtensions={['xlsx', 'xls', 'csv']}
                            />
                        </>
                    )}
                </Flexbox>
                {selectedProjectsCount == 0 && <ViewSelect entity="projects" searchInputRef={inputSearchRef} />}
                {selectedProjectsCount == 0 && (
                    <Flexbox style={{flexShrink: 0}} align="center" gap={2}>
                        <TableFilters
                            onApply={filters => {
                                setSearchParams({filters: JSON.stringify(filters), page: 1})
                                resetSelectionStates()
                            }}
                            filtersParams={searchParams.filters}
                            entity="projects"
                            availableFilters={[
                                {
                                    key: ProjectsColumn.enum.serviceType,
                                    label: columnNameToLabel[ProjectsColumn.enum.serviceType]
                                },
                                {key: ProjectsColumn.enum.sector, label: columnNameToLabel[ProjectsColumn.enum.sector]},
                                {
                                    key: ProjectsColumn.enum.birthDate,
                                    label: columnNameToLabel[ProjectsColumn.enum.birthDate]
                                },
                                {key: ProjectsColumn.enum.age, label: columnNameToLabel[ProjectsColumn.enum.age]},
                                {key: ProjectsColumn.enum.type, label: columnNameToLabel[ProjectsColumn.enum.type]},
                                {key: ProjectsColumn.enum.code, label: columnNameToLabel[ProjectsColumn.enum.code]},
                                {
                                    key: ProjectsColumn.enum.startDate,
                                    label: columnNameToLabel[ProjectsColumn.enum.startDate]
                                },
                                {
                                    key: ProjectsColumn.enum.durationDays,
                                    label: columnNameToLabel[ProjectsColumn.enum.durationDays]
                                },
                                {
                                    key: ProjectsColumn.enum.expirationDate,
                                    label: columnNameToLabel[ProjectsColumn.enum.expirationDate]
                                },
                                {key: ProjectsColumn.enum.status, label: columnNameToLabel[ProjectsColumn.enum.status]},
                                {
                                    key: ProjectsColumn.enum.isRenewed,
                                    label: columnNameToLabel[ProjectsColumn.enum.isRenewed]
                                },
                                {
                                    key: ProjectsColumn.enum.endDate,
                                    label: columnNameToLabel[ProjectsColumn.enum.endDate]
                                },
                                {
                                    key: ProjectsColumn.enum.isSuspended,
                                    label: columnNameToLabel[ProjectsColumn.enum.isSuspended]
                                },
                                {
                                    key: ProjectsColumn.enum.suspensionDays,
                                    label: columnNameToLabel[ProjectsColumn.enum.suspensionDays]
                                },
                                {
                                    key: ProjectsColumn.enum.completedAccessesPerCent,
                                    label: columnNameToLabel[ProjectsColumn.enum.completedAccessesPerCent]
                                },
                                {
                                    key: ProjectsColumn.enum.absenceDays,
                                    label: columnNameToLabel[ProjectsColumn.enum.absenceDays]
                                },
                                {
                                    key: ProjectsColumn.enum.progressStatus,
                                    label: columnNameToLabel[ProjectsColumn.enum.progressStatus]
                                },
                                {
                                    key: ProjectsColumn.enum.openingExportedAt,
                                    label: columnNameToLabel[ProjectsColumn.enum.openingExportedAt]
                                },
                                {
                                    key: ProjectsColumn.enum.openingTransmissionAt,
                                    label: columnNameToLabel[ProjectsColumn.enum.openingTransmissionAt]
                                },
                                {
                                    key: ProjectsColumn.enum.closingExportedAt,
                                    label: columnNameToLabel[ProjectsColumn.enum.closingExportedAt]
                                },
                                {
                                    key: ProjectsColumn.enum.closingTransmissionAt,
                                    label: columnNameToLabel[ProjectsColumn.enum.closingTransmissionAt]
                                },
                                {
                                    key: ProjectsColumn.enum.referringDoctorId,
                                    label: columnNameToLabel[ProjectsColumn.enum.referringDoctorId]
                                },
                                {
                                    key: ProjectsColumn.enum.telephone,
                                    label: columnNameToLabel[ProjectsColumn.enum.telephone]
                                },
                                {
                                    key: ProjectsColumn.enum.taxCode,
                                    label: columnNameToLabel[ProjectsColumn.enum.taxCode]
                                },
                                {
                                    key: 'consultant',
                                    label: 'commons:operator'
                                }
                            ]}
                        />
                        <SortingMenu
                            columns={columnsMap}
                            order={sortParamToPayload(searchParams.sorts) ?? []}
                            setOrder={sorts => {
                                setSearchParams({sorts: JSON.stringify(sorts)})
                                resetSelectionStates()
                            }}
                        />
                        <ColumnVisibilityMenu
                            columns={columnsMap}
                            columnVisibility={columnVisibility}
                            onSubmit={columnVisibility =>
                                setSearchParams({columnVisibility: columnVisibilityToSearchParams(columnVisibility)})
                            }
                        />
                    </Flexbox>
                )}
            </FiltersBar>
            {projectsQuery.isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : (
                <Table
                    emptyStateComponent={
                        <EmptyState
                            title={t('commons:no_projects_title')}
                            subtitle={t('projects:no_projects_description')}
                        >
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                    setSearchParams({search: undefined, filters: undefined, view: undefined, page: 1})
                                    if (inputSearchRef.current) {
                                        inputSearchRef.current.value = ''
                                    }
                                }}
                            >
                                {t('projects:no_projects_cta')}
                            </Button>
                        </EmptyState>
                    }
                    rowComponent={({tableBodyRowProps, dataItem, index}) => (
                        <TableBodyRow
                            onClick={dataItem ? () => navigate(dataItem.id.toString()) : undefined}
                            key={index}
                            {...tableBodyRowProps}
                        />
                    )}
                    pagination={{
                        ...projectsQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: searchParams.limit,
                        onChangePage: page => setSearchParams({page}),
                        onChangeLimit: limit => {
                            resetSelectionStates()
                            setSearchParams({limit, page: 1})
                        }
                    }}
                    columnPinning={{
                        left: [
                            ProjectsColumn.enum.select,
                            ProjectsColumn.enum.fullName,
                            ProjectsColumn.enum.serviceType
                        ],
                        right: [ProjectsColumn.enum.moreActions]
                    }}
                    columnVisibility={columnVisibility}
                    getIsRowSelected={({dataItem}) =>
                        getIsRowSelected({
                            dataItem,
                            selectedIds: selectedProjectsIds,
                            unselectedIds: unselectedProjectsIds,
                            isSelectAll
                        })
                    }
                    isLoading={projectsQuery.isPending}
                    data={projectsQuery.data?.data ?? []}
                    columns={[
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    {permissions?.projects.write && (
                                        <StyledSelectCell>
                                            <SelectRowCheckbox
                                                isSelectAll={isSelectAll}
                                                setSelectedIds={setSelectedProjectsIds}
                                                getIsRowSelected={({dataItem}) =>
                                                    getIsRowSelected({
                                                        dataItem,
                                                        selectedIds: selectedProjectsIds,
                                                        unselectedIds: unselectedProjectsIds,
                                                        isSelectAll
                                                    })
                                                }
                                                dataItem={dataItem}
                                                setUnselectedIds={setUnselectedProjectsIds}
                                            />
                                        </StyledSelectCell>
                                    )}
                                </TableBodyCell>
                            ),
                            header: () =>
                                permissions?.projects.write ? (
                                    <SelectColumnHeader
                                        currentPageIds={projectsQuery.data?.data.map(project => project.id) ?? []}
                                        isSelectAll={isSelectAll}
                                        selectedIds={selectedProjectsIds}
                                        setIsSelectAll={setIsSelectAll}
                                        unselectedIds={unselectedProjectsIds}
                                        setUnselectedIds={setUnselectedProjectsIds}
                                        setSelectedIds={setSelectedProjectsIds}
                                        selectedCount={selectedProjectsCount}
                                        paginationTotal={projectsQuery.persistedPagination.total}
                                    />
                                ) : (
                                    <></>
                                ),
                            id: ProjectsColumn.enum.select,
                            size: () => 64
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText variant="dark" weight="bold">
                                        {permissions?.patients?.read ? (
                                            <Link
                                                to={generatePath(routes.PATIENT_DETAILS_INFORMATION.handle.path, {
                                                    id: dataItem.patient.id
                                                })}
                                                onClick={e => e.stopPropagation()}
                                            >
                                                {dataItem.patient.fullName}
                                            </Link>
                                        ) : (
                                            dataItem.patient.fullName
                                        )}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.fullName]),
                            id: ProjectsColumn.enum.fullName,
                            minSize: () => 80,
                            size: () => 200,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {t(workServiceToLabel[dataItem.patient.serviceType])}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.serviceType]),
                            id: ProjectsColumn.enum.serviceType,
                            size: () => 110
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{t(projectSectorToLabel[dataItem.sector])}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.sector]),
                            id: ProjectsColumn.enum.sector,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {formatLocaleDate(dataItem.patient.birthDate, 'll')}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.birthDate]),
                            id: ProjectsColumn.enum.birthDate,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.patient.age}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.age]),
                            id: ProjectsColumn.enum.age,
                            size: () => 80
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.type ? t(projectTypeToLabel[dataItem.type]) : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.type]),
                            id: ProjectsColumn.enum.type,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.code}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.code]),
                            id: ProjectsColumn.enum.code,
                            size: () => 80
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.startDate ? formatLocaleDate(dataItem.startDate, 'll') : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.startDate]),
                            id: ProjectsColumn.enum.startDate,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.durationDays || '-'}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.durationDays]),
                            id: ProjectsColumn.enum.durationDays,
                            size: () => 80
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.expirationDate
                                            ? formatLocaleDate(dataItem.expirationDate, 'll')
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.expirationDate]),
                            id: ProjectsColumn.enum.expirationDate,
                            size: () => 140
                        },
                        {
                            // Todo: remove the condition `dataItem.status ? ... : '-'` once status is implemented
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledStatusCell>
                                        {dataItem.status ? (
                                            <>
                                                <StatusBullet status={dataItem.status} />
                                                {t(projectStatusToLabel[dataItem.status])}
                                                {!!dataItem.isRenewed && dataItem.type == 'continuous' && (
                                                    <Repeat02Icon size={12} />
                                                )}
                                            </>
                                        ) : (
                                            '-'
                                        )}
                                    </StyledStatusCell>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.status]),
                            id: ProjectsColumn.enum.status,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.isRenewed ? t('commons:yes') : t('commons:no')}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.isRenewed]),
                            id: ProjectsColumn.enum.isRenewed,
                            size: () => 100
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.endDate ? formatLocaleDate(dataItem.endDate, 'll') : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.endDate]),
                            id: ProjectsColumn.enum.endDate,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.isSuspended && dataItem.status != 'opening' ? (
                                            <Flexbox align="center" gap={2}>
                                                {t('commons:yes')}
                                                <AlertCircleIcon size={16} fill={palette.orange['600']} />
                                            </Flexbox>
                                        ) : (
                                            t('commons:no')
                                        )}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.isSuspended]),
                            id: ProjectsColumn.enum.isSuspended,
                            size: () => 100
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => {
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>
                                            {dataItem.numberOfPlannedAccesses ? dataItem.suspensionDays : '-'}
                                        </TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.suspensionDays]),
                            id: ProjectsColumn.enum.suspensionDays,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => {
                                const percentage = dataItem.numberOfPlannedAccesses
                                    ? Math.floor((dataItem.numberOfAccesses / dataItem.numberOfPlannedAccesses) * 100)
                                    : 0
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        {dataItem.numberOfPlannedAccesses ? (
                                            <Flexbox align="center" gap={2} fullWidth>
                                                <ProgressBar rounded percentage={percentage} />
                                                <TableBodyCellText style={{flexShrink: 0}}>
                                                    {percentage}%
                                                </TableBodyCellText>
                                            </Flexbox>
                                        ) : (
                                            <TableBodyCellText>-</TableBodyCellText>
                                        )}
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.completedAccessesPerCent]),
                            id: ProjectsColumn.enum.completedAccessesPerCent,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => {
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>
                                            {dataItem.numberOfPlannedAccesses ? dataItem.absenceDays : '-'}
                                        </TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.absenceDays]),
                            id: ProjectsColumn.enum.absenceDays,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => {
                                if (!dataItem.progressStatus || dataItem.status == 'opening') {
                                    return (
                                        <TableBodyCell {...forwardedProps}>
                                            <StyledProgressCell>-</StyledProgressCell>
                                        </TableBodyCell>
                                    )
                                }

                                const Icon = projectProgressStatusToIcon[dataItem.progressStatus]

                                if (dataItem.progressStatus == 'incomplete') {
                                    return (
                                        <TableBodyCell {...forwardedProps}>
                                            <TableBodyCellText>
                                                <Badge variant="orange" size="sm">
                                                    <Icon size={12} />
                                                    {t(projectProgressStatusToLabel[dataItem.progressStatus])}
                                                </Badge>
                                            </TableBodyCellText>
                                        </TableBodyCell>
                                    )
                                }
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <StyledProgressCell>
                                            <Icon size={12} />
                                            {t(projectProgressStatusToLabel[dataItem.progressStatus])}
                                        </StyledProgressCell>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.progressStatus]),
                            id: ProjectsColumn.enum.progressStatus,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => {
                                if (dataItem.openingExportedAt) {
                                    return (
                                        <TableBodyCell {...forwardedProps}>
                                            <TableBodyCellText>
                                                {formatLocaleDate(dataItem.openingExportedAt, 'll')}
                                            </TableBodyCellText>
                                        </TableBodyCell>
                                    )
                                }
                                if (
                                    dataItem.progressStatus == 'to_print' ||
                                    dataItem.progressStatus == 'to_export' ||
                                    (dataItem.progressStatus === 'transmitted' && !dataItem.openingExportedAt)
                                ) {
                                    return (
                                        <TableBodyCell {...forwardedProps}>
                                            <TableBodyCellText variant="danger">
                                                {t('projects:not_performed')}
                                            </TableBodyCellText>
                                        </TableBodyCell>
                                    )
                                }
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>-</TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.openingExportedAt]),
                            id: ProjectsColumn.enum.openingExportedAt,
                            size: () => 200
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => {
                                if (dataItem.openingTransmissionAt) {
                                    return (
                                        <TableBodyCell {...forwardedProps}>
                                            <TableBodyCellText>
                                                {formatLocaleDate(dataItem.openingTransmissionAt, 'll')}
                                            </TableBodyCellText>
                                        </TableBodyCell>
                                    )
                                }
                                if (
                                    dataItem.progressStatus == 'to_transmit' ||
                                    (dataItem.progressStatus === 'transmitted' && !dataItem.openingTransmissionAt)
                                ) {
                                    return (
                                        <TableBodyCell {...forwardedProps}>
                                            <TableBodyCellText variant="danger">
                                                {t('projects:not_performed')}
                                            </TableBodyCellText>
                                        </TableBodyCell>
                                    )
                                }
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>-</TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.openingTransmissionAt]),
                            id: ProjectsColumn.enum.openingTransmissionAt,
                            size: () => 200
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => {
                                if (dataItem.closingExportedAt) {
                                    return (
                                        <TableBodyCell {...forwardedProps}>
                                            <TableBodyCellText>
                                                {formatLocaleDate(dataItem.closingExportedAt, 'll')}
                                            </TableBodyCellText>
                                        </TableBodyCell>
                                    )
                                }
                                if (
                                    (dataItem.status == 'closed' &&
                                        (dataItem.progressStatus == 'to_print' ||
                                            dataItem.progressStatus == 'to_export')) ||
                                    (dataItem.progressStatus === 'transmitted' && !dataItem.closingExportedAt)
                                ) {
                                    return (
                                        <TableBodyCell {...forwardedProps}>
                                            <TableBodyCellText variant="danger">
                                                {t('projects:not_performed')}
                                            </TableBodyCellText>
                                        </TableBodyCell>
                                    )
                                }
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>-</TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.closingExportedAt]),
                            id: ProjectsColumn.enum.closingExportedAt,
                            size: () => 200
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => {
                                if (dataItem.closingTransmissionAt) {
                                    return (
                                        <TableBodyCell {...forwardedProps}>
                                            <TableBodyCellText>
                                                {formatLocaleDate(dataItem.closingTransmissionAt, 'll')}
                                            </TableBodyCellText>
                                        </TableBodyCell>
                                    )
                                }
                                if (dataItem.status == 'closed' && dataItem.progressStatus == 'to_transmit') {
                                    return (
                                        <TableBodyCell {...forwardedProps}>
                                            <TableBodyCellText variant="danger">
                                                {t('projects:not_performed')}
                                            </TableBodyCellText>
                                        </TableBodyCell>
                                    )
                                }
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>-</TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.closingTransmissionAt]),
                            id: ProjectsColumn.enum.closingTransmissionAt,
                            size: () => 200
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => {
                                const referringDoctor = dataItem.team?.find(
                                    member => member.group === 'referring_doctor'
                                )
                                return referringDoctor ? (
                                    <StyledReferringDoctorCell {...forwardedProps}>
                                        <TableBodyCellText variant="dark">{referringDoctor.fullName}</TableBodyCellText>
                                        <TableBodyCellText>{referringDoctor.specializationName}</TableBodyCellText>
                                    </StyledReferringDoctorCell>
                                ) : (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>-</TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.referringDoctorId]),
                            id: ProjectsColumn.enum.referringDoctorId,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <ContactCell
                                    contacts={dataItem.patient.contacts.filter(
                                        contact => contact.type == 'telephone' || contact.type == 'mobile_phone'
                                    )}
                                    forwardedProps={forwardedProps}
                                />
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.telephone]),
                            id: ProjectsColumn.enum.telephone,
                            size: () => 160,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.patient.taxCode ?? '-'}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[ProjectsColumn.enum.taxCode]),
                            id: ProjectsColumn.enum.taxCode,
                            size: () => 160
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <MoreActionsCell {...forwardedProps}>
                                    <RowMenu
                                        project={dataItem}
                                        onDeleteSuccess={() => {
                                            setSelectedProjectsIds(prev => prev.filter(id => id != dataItem.id))
                                            setUnselectedProjectsIds(prev => prev.filter(id => id != dataItem.id))
                                        }}
                                    />
                                </MoreActionsCell>
                            ),
                            header: () => undefined,
                            id: ProjectsColumn.enum.moreActions,
                            size: () => 56
                        }
                    ]}
                />
            )}
        </StyledWrapper>
    )
}

const ContactCell = ({contacts, forwardedProps}: {contacts: Contact[]; forwardedProps: TableBodyCellProps}) => {
    const {t} = useTranslation()
    const otherContactsCount = contacts.length - 1

    return (
        <StyledContactCell {...forwardedProps}>
            <TableBodyCellText>{contacts[0]?.contact ?? '-'}</TableBodyCellText>
            {otherContactsCount >= 1 && (
                <MenuProvider>
                    <MenuButton onClick={(event: MouseEvent) => event.stopPropagation()}>
                        <Text size="textXs" palette="neutral" shade="500" style={{textDecoration: 'underline'}}>
                            {contacts[0].type === 'telephone'
                                ? t('patients:other_x_telephone_numbers', {count: otherContactsCount})
                                : contacts[0].type === 'email'
                                  ? t('patients:other_x_email_addresses', {count: otherContactsCount})
                                  : t('patients:other_x_mobile_phone_numbers', {count: otherContactsCount})}
                        </Text>
                    </MenuButton>
                    <Menu>
                        <MenuGroup>
                            {contacts.map((contact, index) => (
                                <MenuItem
                                    render={
                                        <a
                                            href={
                                                contact.type == 'email'
                                                    ? `mailto:${contact.contact}`
                                                    : `tel:${contact.contact}`
                                            }
                                        />
                                    }
                                    key={index}
                                >
                                    <PhoneIcon />
                                    {contact.contact}
                                </MenuItem>
                            ))}
                        </MenuGroup>
                    </Menu>
                </MenuProvider>
            )}
        </StyledContactCell>
    )
}
